import { Box, Divider, Skeleton, useMediaQuery } from '@mui/material';

const CarDetailSkeleton = ({ hideLeftSide = false, hideRightSide = false }) => {
  const isMobile = useMediaQuery('(max-width:1300px)');
  return (
    <Box sx={{ display: 'flex', gap: 3, width: '100%', height: 'calc(100vh - 130px)' }}>
      {/* Left sidebar skeleton */}
      {!hideLeftSide && (
        <Box sx={{ width: '350px', flexShrink: 0 }}>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Add To Calendar and Watch List buttons skeleton */}
            <Box display="flex" flexDirection="column" gap={1}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={50}
                sx={{
                  borderRadius: '0.5rem',
                }}
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={50}
                sx={{
                  borderRadius: '0.5rem',
                }}
              />
            </Box>

            {/* Auto Bid section skeleton */}
            <Box
              sx={{
                borderRadius: '0.75rem',
                padding: '1rem',
                border: '1px solid #E0E0E0',
              }}
            >
              <Skeleton variant="text" width="60%" height={20} sx={{ mb: 2 }} />

              {/* Starting Bid */}
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="text" width="40%" height={20} sx={{ mb: 0.5 }} />
                <Skeleton variant="rectangular" width="100%" height={45} sx={{ borderRadius: '0.5rem' }} />
              </Box>

              {/* Maximum Bid */}
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="text" width="50%" height={20} sx={{ mb: 0.5 }} />
                <Skeleton variant="rectangular" width="100%" height={45} sx={{ borderRadius: '0.5rem' }} />
              </Box>

              {/* Delivery Mode */}
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="text" width="50%" height={16} sx={{ mb: 0.5 }} />
                <Box display="flex" gap={1}>
                  <Skeleton variant="rectangular" width={80} height={35} sx={{ borderRadius: '2rem' }} />
                  <Skeleton variant="rectangular" width={80} height={35} sx={{ borderRadius: '2rem' }} />
                </Box>
              </Box>

              {/* Address field */}
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="rectangular" width="100%" height={45} sx={{ borderRadius: '0.5rem' }} />
              </Box>

              {/* Save button */}
              <Skeleton variant="rectangular" width="100%" height={45} sx={{ borderRadius: '0.5rem' }} />
            </Box>

            {/* Sale Information skeleton */}
            <Box
              sx={{
                borderRadius: '0.75rem',
                padding: '1rem',
                border: '1px solid #E0E0E0',
              }}
            >
              <Skeleton variant="text" width="70%" height={30} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="100%" height={18} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="80%" height={18} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="90%" height={18} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="60%" height={18} />
              <Skeleton variant="text" width="80%" height={18} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="90%" height={18} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="60%" height={18} />
              <Skeleton variant="text" width="80%" height={18} sx={{ mb: 0.5 }} />
            </Box>
          </Box>
        </Box>
      )}
      {!hideLeftSide && <Divider orientation={'vertical'} flexItem />}
      {/* Main content area skeleton */}
      {!hideRightSide && (
        <Box sx={{ flex: 1 }}>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Car images carousel skeleton */}
            <Box
              sx={{
                borderRadius: '0.75rem',
                height: '300px',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Skeleton variant="rectangular" width="30%" height="100%" sx={{ borderRadius: '0.5rem' }} />
              <Skeleton variant="rectangular" width="30%" height="100%" sx={{ borderRadius: '0.5rem' }} />
              <Skeleton variant="rectangular" width="30%" height="100%" sx={{ borderRadius: '0.5rem' }} />
              <Skeleton variant="rectangular" width="20%" height="100%" sx={{ borderRadius: '0.5rem' }} />
            </Box>

            {/* Vehicle Information section skeleton */}
            <Box
              sx={{
                borderRadius: '0.75rem',
                padding: '1.5rem',
                border: '1px solid #E0E0E0',
              }}
            >
              {/* Header with title and button */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Skeleton variant="rectangular" width="300px" height={36} sx={{ borderRadius: '0.5rem' }} />
                <Skeleton variant="rectangular" width="140px" height={36} sx={{ borderRadius: '0.5rem' }} />
              </Box>

              {/* Vehicle details in a three-column grid layout */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 3,
                }}
              >
                {Array.from({ length: 3 }).map((_, columnIdx) => (
                  <Box
                    key={columnIdx}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2.5,
                    }}
                  >
                    {Array.from({ length: 4 }).map((__, rowIdx) => (
                      <Box
                        key={`${columnIdx}-${rowIdx}`}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        {/* Label */}
                        <Skeleton variant="text" width="70%" height={18} sx={{ opacity: 0.6 }} />
                        {/* Value */}
                        <Skeleton variant="text" width="85%" height={20} />
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Car Relist Count section skeleton */}
            <Box
              sx={{
                borderRadius: '0.75rem',
                padding: `${32 / 16}rem`,
                border: '1px solid #E0E0E0',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                {[...Array(3)].map((_, index) => (
                  <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
                    <Skeleton
                      key={index}
                      variant="circular"
                      width={60}
                      height={60}
                      sx={{ borderRadius: '80px', mx: 2 }}
                    />
                    {index !== 3 && index !== 2 && <Skeleton variant="text" width="300px" height={10} />}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CarDetailSkeleton;
